import Button from '@pelckmans/elementis/components/button';
import Page from '@pelckmans/elementis/components/page';
import Tabs from '@pelckmans/elementis/components/tabs';
import Plus from '@pelckmans/elementis/icons/plus';
import { isAxiosError } from 'axios';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TABS } from '.';
import IdentityContext from '../../contexts/IdentityContext';
import ModalContext from '../../contexts/ModalContext';
import UserModuleContext from '../../contexts/UserModuleContext';
import Role from '../../core/security/role';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import useStudioGroup from '../../hooks/useStudioGroup';
import useThrowAsyncError from '../../hooks/useThrowAsyncError';
import ContentBody from '../../layout/content/ContentBody';
import ContentHeader from '../../layout/content/ContentHeader';
import AddLicenseModal from '../../shared-components/AddLicenseModal';
import {
  withScrollThreshold,
  WithScrollThresholdProps,
} from '../../shared-components/elementis/hoc/withScrollThreshold';
import { useMarketingNotifications } from '../../shared-components/marketing-notification';
import { buildGroupProps } from '../../utils/elementisUtils';
import useGroupInviteToasts from '../my-teachers/hooks/useGroupInviteToasts';
import {
  fetchPinnedModules,
  fetchVisitedModules,
  removePinnedModule,
  savePinnedModule,
  UserModule,
} from './api/methodsApi';
import EmptyState from './components/EmptyState';
import { MethodList } from './components/MethodList';
import UserRoleAlert from './components/UserRoleAlert';
import type { ModuleGroup } from './utils';
import { mergePhasedOutModulesIntoGroups, sortModules } from './utils';

interface DashboardProps extends WithScrollThresholdProps {}

function Dashboard({ pastThreshold }: DashboardProps) {
  const {
    identity: { isTeacherAlike, highestRole },
  } = useContext(IdentityContext);
  const studioGroup = useStudioGroup();
  const isCodeBasedStudent = (studioGroup === 'VO' || studioGroup === 'BAO') && highestRole === Role.STUDENT;
  const [groupedModules, setGroupedModules] = useState<ModuleGroup[]>([]);
  const [phasedOutModules, setPhasedOutModules] = useState<ModuleGroup[]>([]);
  const [visitedModules, setVisitedModuleIds] = useState<string[]>([]);
  const [pinnedModuleIds, setPinnedModuleIds] = useState<{ id: number; moduleId: string }[]>([]);
  const [isModuleStatusLoading, setIsModuleStatusLoading] = useState(true);
  const [addedModuleIds, setAddedModuleIds] = useState<string[]>([]);
  const { userModules, refetchUserModules, isLoading } = useContext(UserModuleContext);
  const isDashboardLoading = isLoading || isModuleStatusLoading;

  const { showModal } = useContext(ModalContext);
  const { t } = useTranslation();
  const handleError = useThrowAsyncError();

  useDocumentTitle(t('PAGES.MY_METHODS.DASHBOARD.DOCUMENT_TITLE'));
  useMarketingNotifications();

  useGroupInviteToasts();

  const groupAndSetUserModules = useCallback(() => {
    if (highestRole === Role.STUDENT) {
      setGroupedModules(sortModules(userModules, false));
      return;
    }

    const { phasedOut, others } = userModules.reduce<{
      phasedOut: UserModule[];
      others: UserModule[];
    }>(
      (acc, item) => {
        (item.publishingState === 'phasedOut' ? acc.phasedOut : acc.others).push(item);
        return acc;
      },
      { phasedOut: [], others: [] },
    );

    setGroupedModules(sortModules(others, isTeacherAlike));
    setPhasedOutModules(sortModules(phasedOut, isTeacherAlike));
  }, [highestRole, userModules, isTeacherAlike]);

  useEffect(() => {
    if (userModules.length) groupAndSetUserModules();
  }, [groupAndSetUserModules, userModules]);

  useEffect(() => {
    async function fetchModuleStatuses() {
      setIsModuleStatusLoading(true);
      const [visited, pinned] = await Promise.all([fetchVisitedModules(), fetchPinnedModules()]);

      setVisitedModuleIds(visited);
      setPinnedModuleIds(pinned);
      setIsModuleStatusLoading(false);
    }

    fetchModuleStatuses().catch(handleError);
  }, [handleError]);

  const pinnedModules = useMemo(() => {
    const pinnedModuleIdSet = new Set(pinnedModuleIds.map(pinnedModule => pinnedModule.moduleId));

    return mergePhasedOutModulesIntoGroups(groupedModules, phasedOutModules, isTeacherAlike)
      .map(group => ({
        years: group.years,
        modules: group.modules.filter(module => pinnedModuleIdSet.has(module.id)),
      }))
      .filter(group => group.modules.length > 0);
  }, [groupedModules, phasedOutModules, pinnedModuleIds, isTeacherAlike]);

  const handlePinChange = async (moduleId: string, nextPinned: boolean) => {
    if (nextPinned) {
      try {
        const pinnedModule = await savePinnedModule(moduleId);

        setPinnedModuleIds(previous => {
          if (previous.some(item => item.moduleId === moduleId)) {
            return previous;
          }

          return [...previous, pinnedModule];
        });
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) {
          // not found indicates that the module to be pinned does not exist or is not accessible for the user
        } else if (isAxiosError(err) && err.response?.status === 409) {
          // conflict indicates that the module is already pinned
        } else {
          handleError(err);
        }
      }
    } else {
      try {
        const pinnedModule = pinnedModuleIds.find(item => item.moduleId === moduleId);

        if (pinnedModule) {
          await removePinnedModule(pinnedModule.id);

          setPinnedModuleIds(previous => previous.filter(item => item.moduleId !== moduleId));
        }
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) {
          // not found indicates that the pinned module was already removed
        } else {
          handleError(err);
        }
      }
    }
  };

  const refetchAfterModuleAdded = async () => {
    const oldModulesMap = new Map(userModules.map(m => [m.id, m]));
    const newModules = await refetchUserModules();
    const newIds: string[] = [];

    if (!newModules) return;

    for (const m of newModules) {
      const oldModule = oldModulesMap.get(m.id);
      if (!oldModule) {
        newIds.push(m.id);
      } else if (m.subModules.length > oldModule.subModules.length) {
        newIds.push(m.id);
      }
    }

    setAddedModuleIds(newIds);
  };

  const showAddLicenseModal = () =>
    showModal(<AddLicenseModal id="add-license-modal" onRefetch={refetchAfterModuleAdded} />);

  if (isCodeBasedStudent) {
    return (
      <>
        <ContentHeader
          cover={
            <Page.Header
              headerText={t('PAGES.MY_METHODS.HEADER.TITLE')}
              density={pastThreshold ? 'compact' : 'normal'}
            />
          }
        />
        <ContentBody>
          {isDashboardLoading ? (
            <EmptyState
              isLoading
              onRefetch={refetchUserModules}
              aria-label={t('PAGES.MY_METHODS.LOADING_STATES.METHODS')}
            />
          ) : (
            <MethodList
              phasedOutModules={phasedOutModules}
              groupedModules={groupedModules}
              visitedModules={visitedModules}
              addedModuleIds={addedModuleIds}
              pinnedModules={pinnedModules}
              onPinChange={handlePinChange}
            />
          )}
        </ContentBody>
      </>
    );
  }

  return (
    <>
      <ContentHeader
        cover={
          <Page.Header
            density={pastThreshold ? 'compact' : 'normal'}
            headerText={t('PAGES.MY_METHODS.HEADER.TITLE')}
            tabs={
              <Tabs.TabList aria-label={t('PAGES.MY_METHODS.HEADER.TAB_LIST.A11Y_LABEL')}>
                <Tabs.Tab id={TABS.DASHBOARD}>{t('PAGES.MY_METHODS.HEADER.TAB_LIST.TABS.MY_METHODS')}</Tabs.Tab>
                <Tabs.Tab id={TABS.CATALOGUE}>{t('PAGES.MY_METHODS.HEADER.TAB_LIST.TABS.CATALOGUE')}</Tabs.Tab>
              </Tabs.TabList>
            }
            buttonGroupProps={buildGroupProps(
              <Button startIcon={<Plus />} prominence="secondary" onClick={showAddLicenseModal}>
                {t('PAGES.MY_METHODS.HEADER.BUTTONS_GROUP.ACTIVATE_LICENSE')}
              </Button>,
            )}
          />
        }
      />
      <ContentBody alerts={<UserRoleAlert />}>
        <Tabs.TabsContent>
          <Tabs.TabPanel id={TABS.DASHBOARD}>
            {isDashboardLoading || (groupedModules.length === 0 && phasedOutModules.length === 0) ? (
              <EmptyState
                isLoading={isDashboardLoading}
                onRefetch={refetchUserModules}
                aria-label={t('PAGES.MY_METHODS.LOADING_STATES.METHODS')}
              />
            ) : (
              <MethodList
                phasedOutModules={phasedOutModules}
                groupedModules={groupedModules}
                visitedModules={visitedModules}
                addedModuleIds={addedModuleIds}
                pinnedModules={pinnedModules}
                onPinChange={handlePinChange}
              />
            )}
          </Tabs.TabPanel>
        </Tabs.TabsContent>
      </ContentBody>
    </>
  );
}

export default withScrollThreshold(Dashboard);
